import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Loader2, Pencil, RefreshCw, Search, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import WorkScheduleEditor from "@/components/agents/WorkScheduleEditor";
import { cloneWorkSchedule } from "@/utils/workSchedule";

const WorkSchedulesSettings = ({
  apiBase,
  isDark,
  textClass,
  labelClass,
  cardClass,
  inputClass,
  companies = [],
  isSuperAdmin = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [agents, setAgents] = useState([]);
  const [summary, setSummary] = useState({ total: 0, configured: 0, missing: 0 });
  const [companyFilter, setCompanyFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkSchedule, setBulkSchedule] = useState(() => cloneWorkSchedule());
  const [editAgent, setEditAgent] = useState(null);
  const [editSchedule, setEditSchedule] = useState(() => cloneWorkSchedule());

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (isSuperAdmin && companyFilter !== "all") params.append("company_id", companyFilter);
      if (statusFilter === "has") params.append("filter", "has");
      if (statusFilter === "missing") params.append("filter", "missing");
      const res = await axios.get(`${apiBase}/agent-work-schedules?${params}`);
      setAgents(res.data?.agents || []);
      setSummary(res.data?.summary || { total: 0, configured: 0, missing: 0 });
      setSelectedIds([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Mesai planları yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [apiBase, companyFilter, isSuperAdmin, statusFilter]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const filteredAgents = useMemo(() => {
    if (!searchTerm.trim()) return agents;
    const term = searchTerm.toLowerCase().trim();
    return agents.filter((a) =>
      (a.ad_soyad && a.ad_soyad.toLowerCase().includes(term)) ||
      (a.company_name && a.company_name.toLowerCase().includes(term))
    );
  }, [agents, searchTerm]);

  const allFilteredSelected = filteredAgents.length > 0 && filteredAgents.every((a) => selectedIds.includes(a.id));
  const someFilteredSelected = filteredAgents.some((a) => selectedIds.includes(a.id));

  const toggleSelectAll = (checked) => {
    if (checked === true) {
      setSelectedIds(filteredAgents.map((a) => a.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleAgent = (agentId) => {
    setSelectedIds((prev) => (
      prev.includes(agentId) ? prev.filter((id) => id !== agentId) : [...prev, agentId]
    ));
  };

  const applyBulk = async ({ applyAll = false, onlyMissing = false, agentIds = null } = {}) => {
    setSaving(true);
    try {
      const payload = {
        work_schedule: bulkSchedule,
        apply_all: applyAll,
        only_missing: onlyMissing,
      };
      if (agentIds && agentIds.length > 0) payload.agent_ids = agentIds;
      if (isSuperAdmin && companyFilter !== "all") payload.company_id = parseInt(companyFilter, 10);
      const res = await axios.put(`${apiBase}/agent-work-schedules`, payload);
      toast.success(`${res.data?.updated || 0} agent mesai planı güncellendi`);
      fetchSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Güncelleme başarısız");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (agent) => {
    setEditAgent(agent);
    setEditSchedule(cloneWorkSchedule(agent.work_schedule));
  };

  const saveSingle = async () => {
    if (!editAgent) return;
    setSaving(true);
    try {
      await axios.put(`${apiBase}/agent-work-schedules`, {
        work_schedule: editSchedule,
        agent_ids: [editAgent.id],
      });
      toast.success(`${editAgent.ad_soyad} mesai planı güncellendi`);
      setEditAgent(null);
      fetchSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className={cardClass}>
        <CardHeader className="pb-4">
          <CardTitle className={`text-lg font-semibold ${textClass}`}>
            <Clock className="w-5 h-5 inline mr-2 text-amber-400" />
            Çalışma Günleri ve Saatleri
          </CardTitle>
          <p className={`text-sm mt-1 ${labelClass}`}>
            Agent mesai planlarını toplu veya tekil güncelleyin. Eksik planları görüp tek seferde ekleyebilirsiniz.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Badge className={isDark ? "bg-slate-700/60 text-slate-200" : "bg-gray-100 text-gray-700"}>
              Toplam: {summary.total}
            </Badge>
            <Badge className={isDark ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-emerald-50 text-emerald-700 border-emerald-200"}>
              Tanımlı: {summary.configured}
            </Badge>
            <Badge className={isDark ? "bg-amber-500/20 text-amber-300 border-amber-500/30" : "bg-amber-50 text-amber-700 border-amber-200"}>
              Eksik: {summary.missing}
            </Badge>
          </div>

          <WorkScheduleEditor
            value={bulkSchedule}
            onChange={setBulkSchedule}
            isDark={isDark}
            hint="Aşağıdaki plan şablonu toplu uygulama butonlarıyla agentlara yazılır."
          />

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => applyBulk({ agentIds: selectedIds })}
              disabled={saving || selectedIds.length === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Seçilenlere uygula ({selectedIds.length})
            </Button>
            <Button
              variant="outline"
              onClick={() => applyBulk({ onlyMissing: true, applyAll: true })}
              disabled={saving || summary.missing === 0}
              className={isDark ? "border-slate-700" : ""}
            >
              Eksik olanların tümüne uygula
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (!window.confirm("Tüm agentlerin mesai planı güncellenecek. Devam edilsin mi?")) return;
                applyBulk({ applyAll: true });
              }}
              disabled={saving || summary.total === 0}
              className={isDark ? "border-slate-700" : ""}
            >
              Tüm agentlere uygula
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className={cardClass}>
        <CardHeader className="pb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className={`text-lg font-semibold ${textClass}`}>
            <Users className="w-5 h-5 inline mr-2 text-indigo-400" />
            Agent Mesai Durumu
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {isSuperAdmin && (
              <Select value={companyFilter} onValueChange={setCompanyFilter}>
                <SelectTrigger className={`h-9 w-40 text-sm ${inputClass}`}><SelectValue /></SelectTrigger>
                <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                  <SelectItem value="all">Tüm Firmalar</SelectItem>
                  {companies.filter((c) => c.status === 1).map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={`h-9 w-36 text-sm ${inputClass}`}><SelectValue /></SelectTrigger>
              <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="has">Tanımlı</SelectItem>
                <SelectItem value="missing">Eksik</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${labelClass}`} />
              <Input
                placeholder="Agent ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-9 h-9 w-44 ${inputClass}`}
              />
            </div>
            <Button variant="outline" size="sm" onClick={fetchSchedules} disabled={loading} className={isDark ? "border-slate-700" : ""}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className={`text-center py-12 ${labelClass}`}><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Yükleniyor...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={isDark ? "border-slate-800" : ""}>
                  <TableHead className={`w-10 ${isDark ? "text-slate-400" : ""}`}>
                    <Checkbox
                      checked={allFilteredSelected ? true : someFilteredSelected ? "indeterminate" : false}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead className={isDark ? "text-slate-400" : ""}>Agent</TableHead>
                  {isSuperAdmin && <TableHead className={isDark ? "text-slate-400" : ""}>Firma</TableHead>}
                  <TableHead className={isDark ? "text-slate-400" : ""}>Durum</TableHead>
                  <TableHead className={isDark ? "text-slate-400" : ""}>Plan Özeti</TableHead>
                  <TableHead className={isDark ? "text-slate-400" : ""}>İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAgents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isSuperAdmin ? 6 : 5} className={`text-center py-10 ${labelClass}`}>
                      Agent bulunamadı
                    </TableCell>
                  </TableRow>
                ) : filteredAgents.map((agent) => (
                  <TableRow key={agent.id} className={isDark ? "border-slate-800/50 hover:bg-slate-800/30" : "hover:bg-gray-50"}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.includes(agent.id)}
                        onCheckedChange={() => toggleAgent(agent.id)}
                      />
                    </TableCell>
                    <TableCell className={`font-medium ${isDark ? "text-slate-200" : ""}`}>{agent.ad_soyad}</TableCell>
                    {isSuperAdmin && (
                      <TableCell className={isDark ? "text-slate-300" : ""}>{agent.company_name || "—"}</TableCell>
                    )}
                    <TableCell>
                      <Badge className={agent.has_work_schedule
                        ? (isDark ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-emerald-50 text-emerald-700 border-emerald-200")
                        : (isDark ? "bg-amber-500/20 text-amber-300 border-amber-500/30" : "bg-amber-50 text-amber-700 border-amber-200")
                      }>
                        {agent.has_work_schedule ? "Tanımlı" : "Eksik"}
                      </Badge>
                    </TableCell>
                    <TableCell className={`text-sm ${isDark ? "text-slate-400" : "text-gray-600"}`}>
                      {agent.schedule_summary || "—"}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(agent)} className={isDark ? "text-slate-400" : ""}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editAgent} onOpenChange={(open) => { if (!open) setEditAgent(null); }}>
        <DialogContent className={`max-w-2xl max-h-[90vh] overflow-y-auto ${isDark ? "bg-slate-900 border-slate-800 text-white" : ""}`}>
          <DialogHeader>
            <DialogTitle>{editAgent?.ad_soyad} — Mesai Planı</DialogTitle>
          </DialogHeader>
          <WorkScheduleEditor
            value={editSchedule}
            onChange={setEditSchedule}
            isDark={isDark}
            compact
            showTitle={false}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditAgent(null)} className={isDark ? "text-slate-300" : ""}>İptal</Button>
            <Button onClick={saveSingle} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Kaydediliyor</> : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkSchedulesSettings;
